import { useEffect, useState } from 'react';
import { quantizeArtworkPixels, rgbTupleToHex, sampleArtworkPixels, type RGBTuple } from '@/utils/brandPalette';

const FORMATS = { rgb: 'rgb', hex: 'hex' } as const;
const DEFAULT_SETTINGS = {
  colors: 5,
  cors: false,
  windowSize: 50,
  format: FORMATS.hex as keyof typeof FORMATS,
  sampleRegion: 'full' as 'full' | 'edges',
};

interface UseImageColorSettings {
  colors?: number;
  cors?: boolean;
  windowSize?: number;
  format?: keyof typeof FORMATS;
  sampleRegion?: 'full' | 'edges';
}

interface UseImageColorReturn {
  colors: string[] | RGBTuple[];
}

export default function useImageColor(
  src: string,
  _settings: UseImageColorSettings = {}
): UseImageColorReturn {
  const settings = { ...DEFAULT_SETTINGS, ..._settings };
  const [colors, setColors] = useState<string[] | RGBTuple[]>([]);

  if (!FORMATS[settings.format]) {
    throw new Error('Invalid output format');
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !src) {
      return;
    }

    const canvas = document.createElement('canvas');
    const img = document.createElement('img');
    const context = canvas.getContext('2d');

    if (!context) {
      console.error('Could not get canvas context');
      return;
    }

    if (settings.cors) {
      img.setAttribute('crossOrigin', '');
    }

    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      context.drawImage(img, 0, 0);
      const { data } = context.getImageData(0, 0, img.naturalWidth, img.naturalHeight);
      const palette = quantizeArtworkPixels(
        sampleArtworkPixels(data, img.naturalWidth, img.naturalHeight, settings),
        settings.colors
      );
      if (palette.length) {
        setColors(settings.format === FORMATS.rgb ? palette : palette.map(rgbTupleToHex));
      }
    };

    img.src = src;
  }, [src, settings.cors, settings.colors, settings.format, settings.sampleRegion, settings.windowSize]);

  return { colors };
}
